/**
 * Utility functions for the connector activation configuration flow.
 *
 * These pure functions determine whether an activation needs to be intercepted
 * for volume/pressure configuration and extract the relevant asset data.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DynamicStorageAssetConfig {
  assetId: string
  assetName: string
  defaultVolume: number | null
  defaultMinPressure: number | null
}

export interface AssetVolumeConfig {
  assetId: string
  volume: number | null
  minPressure: number | null
}

// ─── Input Types ──────────────────────────────────────────────────────────────

/** Minimal shape for an asset that can be checked for dynamic_storage kind. */
export interface AssetWithKind {
  kind?: string | null
}

/** Shape accepted by getDynamicStorageAssets — accommodates both system view
 *  assets and group children with varying field naming conventions. */
export interface AssetInput {
  id?: string
  assetId?: string
  name?: string
  assetName?: string
  kind?: string | null
  volume?: number | null
  min_pressure?: number | null
  minPressure?: number | null
}

// ─── Validation ───────────────────────────────────────────────────────────────

/**
 * Validates a single volume/pressure pair for the activation dialog.
 * Returns true iff volume is a finite number > 0 and minPressure is a finite number >= 0.
 */
export function isValidVolumeConfig(volume: string, minPressure: string): boolean {
  const volumeNum = parseFloat(volume)
  const pressureNum = parseFloat(minPressure)
  return (
    !isNaN(volumeNum) &&
    isFinite(volumeNum) &&
    volumeNum > 0 &&
    !isNaN(pressureNum) &&
    isFinite(pressureNum) &&
    pressureNum >= 0
  )
}

// ─── Functions ────────────────────────────────────────────────────────────────

/**
 * Filters an array of assets to those with `kind === "dynamic_storage"` and
 * returns configuration objects with existing volume/pressure defaults.
 *
 * Handles both `min_pressure` (snake_case from backend) and `minPressure`
 * (camelCase from frontend types) field names.
 */
export function getDynamicStorageAssets(assets: AssetInput[]): DynamicStorageAssetConfig[] {
  return assets
    .filter((asset): asset is AssetInput & { kind: "dynamic_storage" } => asset.kind === "dynamic_storage")
    .map((asset) => ({
      assetId: asset.assetId ?? asset.id ?? "",
      assetName: asset.assetName ?? asset.name ?? "",
      defaultVolume: asset.volume ?? null,
      defaultMinPressure: asset.minPressure ?? asset.min_pressure ?? null,
    }))
}

/**
 * Determines whether the activation flow should be intercepted to show the
 * configuration dialog.
 *
 * Returns `true` if:
 * - The connector type's `requiresActivationConfig` flag is set, OR
 * - At least one asset in the set has `kind === "dynamic_storage"`
 */
export function needsActivationConfig(
  requiresActivationConfig: boolean,
  assets: AssetWithKind[],
): boolean {
  if (requiresActivationConfig) return true
  return assets.some((asset) => asset.kind === "dynamic_storage")
}
